import { Alert } from '@creditornot/cb-alert';
import { Button, TextButton } from '@creditornot/cb-button';
import { colors } from '@creditornot/cb-ingredients';
import { ToastVariant } from '@creditornot/cb-toast';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { useLocalStorage, usePluralization } from 'common/hooks';

import { fetchITAEnv, TokenStatus } from './utils';

type TokenAlertProps = {
  status: string;
  merchantId: string;
  daysAfterTokenExpires: number;
  handleModalOpen: () => void;
};

const StyledLink = styled.a`
  max-width: max-content;
  align-self: flex-end;
  color: ${colors.wolt100};
  text-decoration: 'none';
`;

const AlertContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledAlert = styled(Alert)`
  border-radius: 0;
`;

export const TokenAlert = ({ status, merchantId, daysAfterTokenExpires, handleModalOpen }: TokenAlertProps) => {
  const { formatMessage } = useIntl();
  const { FormattedPluralMessage } = usePluralization();
  const { itaUri, clientId, redirectUri } = fetchITAEnv();
  const [_, setMerchantId] = useLocalStorage('merchantId', '');

  const alertContent = (messageId: string, variant: TokenStatus, count?: number) => {
    return (
      <AlertContent>
        <p>
          {count ? <FormattedPluralMessage id={messageId} count={count} /> : <FormattedMessage id={messageId} />}{' '}
          {variant === TokenStatus.INITIAL && (
            <TextButton data-test-id="learn-more" variant="brand" onClick={handleModalOpen}>
              <FormattedMessage id="tax-reporting.learn-more" />
            </TextButton>
          )}
        </p>
        <StyledLink
          href={`${itaUri}?response_type=code&client_id=${clientId}&scope=scope&redirect_uri=${redirectUri}`}
          target="_blank"
          rel="noreferrer"
        >
          <Button size="small" variant="primary" onClick={() => setMerchantId(merchantId)}>
            <FormattedMessage id="tax-reporting.login-button" />
          </Button>
        </StyledLink>
      </AlertContent>
    );
  };

  switch (status) {
    case TokenStatus.INFO:
      return (
        <StyledAlert
          data-test-id="info-alert"
          variant={status as ToastVariant}
          title={formatMessage({ id: 'tax-reporting.state.info.title' })}
        >
          {alertContent('tax-reporting.state.info.message', status)}
        </StyledAlert>
      );
    case TokenStatus.WARNING:
      return (
        <StyledAlert
          data-test-id="warning-alert"
          variant={status as ToastVariant}
          title={formatMessage({ id: 'tax-reporting.title' })}
        >
          {alertContent('tax-reporting.state.warning.message', status, daysAfterTokenExpires)}
        </StyledAlert>
      );
    case TokenStatus.ERROR:
      return (
        <StyledAlert
          data-test-id="error-alert"
          variant={status as ToastVariant}
          title={formatMessage({ id: 'tax-reporting.state.error.title' })}
        >
          {alertContent('tax-reporting.state.error.message', status)}
        </StyledAlert>
      );
    default:
      return (
        <StyledAlert
          data-test-id="initial-alert"
          variant={'info' as ToastVariant}
          title={formatMessage({ id: 'tax-reporting.title' })}
        >
          {alertContent('tax-reporting.state.initial.message', status as TokenStatus)}
        </StyledAlert>
      );
  }
};
